import { LinearProgress } from '@mui/material'
import { Dispatch, SetStateAction, useCallback, useEffect, useRef, useState } from 'react'
import { Checkbox } from '~/@/components/ui/checkbox'
import {
  GetBulkActionEligibilityCountsQuery,
  TableFilterInput,
  useExecuteBulkActionMutation,
  useGetSelectedLookupRowsLazyQuery,
} from '~/common/generated/apollo-operations'
import { isButtonDestructive } from '~/common/utils/button'
import { ConfirmationDialog } from '~/components/ConfirmationDialog'
import { useDossSnackbar } from '~/components/shared/DossSnackbar'
import { DetailedTable, DetailedTableRow } from '~/components/tables/types'
import { useRowProcessingState } from './useRowProcessingState'

type ButtonEligibilityCount =
  GetBulkActionEligibilityCountsQuery['bulkActionEligibilityCounts']['buttonEligibilityCounts'][number]

const PAGE_SIZE = 50

export interface BulkActionPreviewDialogProps {
  table: DetailedTable
  button: ButtonEligibilityCount
  eligibleRowIds: string[]
  isSelectAll: boolean
  currentFilters?: TableFilterInput[]
  searchString?: string
  onClose: () => void
  onClearSelection: () => void
  setRowsData: Dispatch<SetStateAction<DetailedTableRow[]>>
}

export function BulkActionPreviewDialog({
  table,
  button,
  eligibleRowIds,
  onClose,
  onClearSelection,
  setRowsData,
}: BulkActionPreviewDialogProps) {
  const snackbar = useDossSnackbar()

  // Local selection state - track which rows are selected (initially all)
  const [selectedRowIds, setSelectedRowIds] = useState<Set<string>>(new Set(eligibleRowIds))
  const selectedCount = selectedRowIds.size

  // Pagination state
  const [loadedRows, setLoadedRows] = useState<
    NonNullable<ReturnType<typeof useGetSelectedLookupRowsLazyQuery>[1]['data']>['tableRows']
  >([])
  const [loadedRowIds, setLoadedRowIds] = useState<Set<string>>(new Set())
  const [hasMore, setHasMore] = useState(true)
  const [isLoadingMore, setIsLoadingMore] = useState(false)
  const isLoadingRef = useRef(false)
  const scrollContainerRef = useRef<HTMLDivElement>(null)

  // Fetch rows
  const [fetchRows, { loading: loadingRows }] = useGetSelectedLookupRowsLazyQuery({
    fetchPolicy: 'no-cache',
  })

  const [executeBulkAction, { loading: executing }] = useExecuteBulkActionMutation()
  const { markRowsProcessing, clearRowsProcessing } = useRowProcessingState(setRowsData)

  const loadMoreRows = useCallback(async () => {
    if (isLoadingRef.current || !hasMore) {
      return
    }
    isLoadingRef.current = true
    setIsLoadingMore(true)

    try {
      // Find the next batch of row IDs to load
      const unloadedRowIds = eligibleRowIds.filter((id) => !loadedRowIds.has(id))
      if (unloadedRowIds.length === 0) {
        setHasMore(false)
        return
      }

      const nextBatch = unloadedRowIds.slice(0, PAGE_SIZE)

      const result = await fetchRows({
        variables: {
          rowIds: nextBatch,
        },
      })

      const newRows = result.data?.tableRows ?? []
      setLoadedRows((prev) => [...prev, ...newRows])
      setLoadedRowIds((prev) => {
        const next = new Set(prev)
        nextBatch.forEach((id) => next.add(id))
        return next
      })

      if (unloadedRowIds.length <= PAGE_SIZE) {
        setHasMore(false)
      }
    } finally {
      isLoadingRef.current = false
      setIsLoadingMore(false)
    }
  }, [eligibleRowIds, fetchRows, hasMore, loadedRowIds])

  // Load initial batch on mount
  useEffect(() => {
    if (eligibleRowIds.length > 0 && loadedRows.length === 0) {
      loadMoreRows()
    }
  }, [eligibleRowIds, loadMoreRows, loadedRows.length])

  // Handle infinite scroll
  const handleScroll = useCallback(() => {
    const container = scrollContainerRef.current
    if (!container || isLoadingMore || !hasMore) {
      return
    }

    const { scrollTop, scrollHeight, clientHeight } = container
    if (scrollHeight - scrollTop - clientHeight < 200) {
      loadMoreRows()
    }
  }, [hasMore, isLoadingMore, loadMoreRows])

  const rows = loadedRows

  const columns = [...table.columns].sort((a, b) => a.order - b.order)

  const isDestructive = isButtonDestructive(button)

  // Toggle row selection
  const toggleRow = (rowId: string) => {
    setSelectedRowIds((prev) => {
      const next = new Set(prev)
      if (next.has(rowId)) {
        next.delete(rowId)
      } else {
        next.add(rowId)
      }
      return next
    })
  }

  // Toggle all rows
  const toggleAll = () => {
    if (selectedRowIds.size === rows.length) {
      setSelectedRowIds(new Set())
    } else {
      setSelectedRowIds(new Set(rows.map((r) => r.id)))
    }
  }

  // Get cell value for a column
  const getCellValue = (row: (typeof rows)[0], columnId: string): string => {
    const cell = row.cells.find((c) => c.column.id === columnId)
    return cell?.userVisibleValue ?? ''
  }

  // Execute the bulk action
  const executeAction = async () => {
    if (!button.manualTrigger) {
      snackbar.showError('This button does not have a manual trigger')
      return
    }

    const rowIds = Array.from(selectedRowIds)

    // Mark the rows as processing up front. From here on we only ever clear this state — the
    // pushRowButtonNodes subscription clears executed rows when the action completes, and a fast
    // action's clear event can arrive before the mutation response, so re-asserting the state
    // from the response would re-arm the spinner permanently (INC-1970).
    markRowsProcessing(rowIds, button)

    try {
      // Always pass explicit row IDs from the preview dialog since we've already
      // pre-filtered to eligible rows. The isSelectAll mode should only be used
      // when executing directly without previewing the eligible rows first.
      const result = await executeBulkAction({
        variables: {
          input: {
            tableId: table.id,
            workspaceButtonId: button.id,
            rowIds,
          },
        },
      })

      const { executedCount, skippedCount, errors } = result.data?.executeBulkAction ?? {}

      // Rows the server skipped never had a processing button set in the database, so no clear
      // event will ever arrive for them — clear their spinners here.
      const executedRowIdSet = new Set(result.data?.executeBulkAction.executedRowIds ?? [])
      clearRowsProcessing(rowIds.filter((rowId) => !executedRowIdSet.has(rowId)))

      if (executedCount && executedCount > 0) {
        snackbar.showSuccess(
          `Successfully triggered action on ${executedCount} row${executedCount === 1 ? '' : 's'}`,
        )
      }

      if (skippedCount && skippedCount > 0) {
        snackbar.showSuccess(`${skippedCount} row${skippedCount === 1 ? ' was' : 's were'} skipped`)
      }

      if (errors && errors.length > 0) {
        snackbar.showError(`${errors.length} error${errors.length === 1 ? '' : 's'} occurred`)
      }

      onClearSelection()
      onClose()
    } catch (error) {
      // Nothing was triggered, so no clear event will arrive — remove the spinners.
      clearRowsProcessing(rowIds)
      snackbar.showError(error instanceof Error ? error.message : 'An error occurred')
    }
  }

  const allSelected = selectedRowIds.size === rows.length && rows.length > 0

  return (
    <ConfirmationDialog
      checkboxText={button.confirmationMessage ?? undefined}
      className="z-[1400]"
      confirmLabel={button.name}
      description={'This action will be applied to all selected items'}
      fullScreen
      isLoading={executing}
      isOpen={true}
      onClose={onClose}
      onConfirm={executeAction}
      title={`${button.name} for ${selectedCount} records`}
      variant={isDestructive ? 'destructive' : 'default'}
    >
      <div
        ref={scrollContainerRef}
        className="relative flex-1 overflow-auto"
        onScroll={handleScroll}
      >
        {/* Table structure - always visible */}
        <table className="min-w-full border-separate border-spacing-0">
          <thead className="sticky top-0 z-10">
            <tr>
              <th className="w-8 min-w-[32px] max-w-[32px] border border-gray-4 bg-gray-2 px-2 py-1">
                <Checkbox
                  checked={allSelected}
                  className="ml-px size-3.5"
                  onCheckedChange={toggleAll}
                  variant="secondary"
                />
              </th>
              {columns.map((col) => (
                <th
                  key={col.id}
                  className="whitespace-nowrap border border-l-0 border-gray-4 bg-gray-2 px-2 py-1 text-left text-sm font-medium text-gray-12"
                >
                  {col.name}
                </th>
              ))}
            </tr>
            {/* Loading overlay - positioned under header (for both initial and pagination loading) */}
            {(loadingRows || isLoadingMore) && (
              <tr>
                <td className="p-0" colSpan={columns.length + 1}>
                  <LinearProgress />
                </td>
              </tr>
            )}
          </thead>
          <tbody>
            {rows.map((row) => (
              <tr key={row.id} className="bg-white hover:bg-gray-1">
                <td className="w-8 min-w-[32px] max-w-[32px] border border-t-0 border-gray-3 px-2 py-1">
                  <Checkbox
                    checked={selectedRowIds.has(row.id)}
                    className="ml-px size-3.5"
                    onCheckedChange={() => toggleRow(row.id)}
                    variant="secondary"
                  />
                </td>
                {columns.map((col) => (
                  <td
                    key={col.id}
                    className="whitespace-nowrap border border-l-0 border-t-0 border-gray-3 px-2 py-1 text-sm text-gray-12"
                  >
                    {getCellValue(row, col.id)}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </ConfirmationDialog>
  )
}
